
import { useState, useEffect } from "react";
import { Achievement } from "@/hooks/use-achievements";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { X, Trophy } from "lucide-react";

interface AchievementNotificationProps {
  achievements: Achievement[];
  onDismiss: () => void;
}

export function AchievementNotification({ achievements, onDismiss }: AchievementNotificationProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (achievements.length > 0) {
      setIsVisible(true);
      setCurrentIndex(0);
    }
  }, [achievements]);

  useEffect(() => {
    if (achievements.length > 0 && currentIndex < achievements.length) {
      const timer = setTimeout(() => {
        if (currentIndex < achievements.length - 1) {
          setCurrentIndex(prev => prev + 1);
        } else {
          // Show all achievements for a bit longer, then dismiss
          setTimeout(() => {
            setIsVisible(false);
            setTimeout(onDismiss, 300);
          }, 2000);
        }
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [currentIndex, achievements.length, onDismiss]);

  if (!isVisible || achievements.length === 0) return null;

  const currentAchievement = achievements[currentIndex];

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-in-from-right">
      <Card className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white shadow-lg border-0 min-w-80">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-white/20 rounded-full">
              <Trophy className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-lg">Achievement Unlocked!</h4>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-2xl">{currentAchievement.icon}</span>
                <div>
                  <p className="font-semibold">{currentAchievement.title}</p>
                  <p className="text-sm opacity-90">{currentAchievement.description}</p>
                </div>
              </div>
              {achievements.length > 1 && (
                <div className="mt-2 text-xs opacity-75">
                  {currentIndex + 1} of {achievements.length} new achievements
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-white hover:bg-white/20"
              onClick={() => {
                setIsVisible(false);
                setTimeout(onDismiss, 300);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
